import { useQuery } from '@tanstack/react-query';
import { MenuItem, TextField, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { listZones } from '../../services/zoneService';

interface Props {
  label?: string;
  value: string;
  onChange: (code: string) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
}

export function useZoneCodeMap() {
  const { data } = useQuery({
    queryKey: ['zones-code-map'],
    queryFn: () => listZones({ limit: 500 }),
    staleTime: 60_000,
  });
  const map = new Map<string, string>();
  (data?.rows || []).forEach((z) => map.set(z.code, z.name));
  return map;
}

export default function ZoneMasterSelect({
  label = 'Zone',
  value,
  onChange,
  required,
  disabled,
  helperText,
  error,
}: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['zones-master-select'],
    queryFn: () => listZones({ limit: 500 }),
    staleTime: 60_000,
  });

  const zones = data?.rows || [];

  if (!isLoading && !zones.length) {
    return (
      <>
        <TextField
          fullWidth
          size="small"
          label={label}
          value={value}
          required={required}
          disabled={disabled}
          error={error}
          onChange={(e) => onChange(e.target.value)}
          placeholder="No zones yet — create in Zone Management"
          helperText={helperText}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          <Link component={RouterLink} to="/zones">Open Zone Management</Link> to add operational zones first.
        </Typography>
      </>
    );
  }

  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={value}
      required={required}
      disabled={disabled || isLoading}
      error={error}
      onChange={(e) => onChange(e.target.value)}
      helperText={helperText || 'Select from configured operational zones'}
    >
      <MenuItem value="">— Select zone —</MenuItem>
      {zones.map((z) => (
        <MenuItem key={z._id} value={z.code}>
          {z.name} ({z.code})
        </MenuItem>
      ))}
    </TextField>
  );
}
